import {
  IconRefresh,
  IconMessage,
  IconCheck,
  IconClock,
  IconX,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { createFileRoute } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { useAgentMySMS, useAgentSMSStats } from "@/hooks/use-agent-sms-relay";
import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";

export const Route = createFileRoute("/sms/")({
  component: AgentSMSPage,
});

function AgentSMSPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <AgentSMSContent />
      </AppShell>
    </ProtectedRoute>
  );
}

function AgentSMSContent() {
  const { data: smsData, isLoading: smsLoading, refetch } = useAgentMySMS({ limit: 100 });
  const { data: stats, isLoading: statsLoading } = useAgentSMSStats();

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "matched":
        return "default";
      case "pending":
        return "secondary";
      case "failed":
        return "destructive";
      default:
        return "outline";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "matched":
        return <IconCheck className="w-3 h-3" />;
      case "pending":
        return <IconClock className="w-3 h-3" />;
      case "failed":
        return <IconX className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const getOperatorColor = (operator: string) => {
    switch (operator.toLowerCase()) {
      case "bkash":
        return "text-pink-500";
      case "nagad":
        return "text-orange-500";
      case "rocket":
        return "text-purple-500";
      default:
        return "text-gray-500";
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">SMS Relay</h1>
          <p className="text-muted-foreground mt-1">
            View SMS messages captured from your devices
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => refetch()}
          disabled={smsLoading}
        >
          <IconRefresh className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* SMS Stats */}
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total Captured</CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-9 w-16 animate-pulse bg-muted rounded" />
            ) : (
              <div className="text-2xl font-bold">{stats?.total_captured || 0}</div>
            )}
            <p className="text-xs text-muted-foreground mt-1">All time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Matched</CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-9 w-16 animate-pulse bg-muted rounded" />
            ) : (
              <div className="text-2xl font-bold text-green-600">
                {stats?.successfully_matched || 0}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">Successfully</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-9 w-16 animate-pulse bg-muted rounded" />
            ) : (
              <div className="text-2xl font-bold text-orange-600">
                {stats?.pending || 0}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">Awaiting match</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-9 w-16 animate-pulse bg-muted rounded" />
            ) : (
              <div className="text-2xl font-bold text-red-600">
                {stats?.failed || 0}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">Matching failed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Today</CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-9 w-16 animate-pulse bg-muted rounded" />
            ) : (
              <div className="text-2xl font-bold text-blue-600">
                {stats?.today?.captured || 0}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.today?.matched || 0} matched
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-9 w-16 animate-pulse bg-muted rounded" />
            ) : (
              <div className="text-2xl font-bold text-purple-600">
                {stats?.total_captured
                  ? ((stats.successfully_matched / stats.total_captured) * 100).toFixed(1)
                  : "0"}
                %
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">Match rate</p>
          </CardContent>
        </Card>
      </div>

      {/* By Operator Stats */}
      {stats?.by_operator && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-pink-500">bKash</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold">{stats.by_operator.bkash?.matched || 0} / {stats.by_operator.bkash?.total || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Matched / Total</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-orange-500">Nagad</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold">{stats.by_operator.nagad?.matched || 0} / {stats.by_operator.nagad?.total || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Matched / Total</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-purple-500">Rocket</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold">{stats.by_operator.rocket?.matched || 0} / {stats.by_operator.rocket?.total || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Matched / Total</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* SMS List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <IconMessage className="w-5 h-5" />
            SMS Messages
          </CardTitle>
          <CardDescription>
            Recent SMS messages captured from your devices
          </CardDescription>
        </CardHeader>
        <CardContent>
          {smsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading SMS messages...</p>
            </div>
          ) : !smsData?.sms || smsData.sms.length === 0 ? (
            <div className="text-center py-8">
              <IconMessage className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No SMS messages yet</p>
              <p className="text-sm text-muted-foreground">
                SMS messages captured from your devices will appear here
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Transaction ID</TableHead>
                    <TableHead>Sender</TableHead>
                    <TableHead>Agent Number</TableHead>
                    <TableHead>Operator</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {smsData.sms.map((sms) => (
                    <TableRow key={sms.id}>
                      <TableCell className="text-sm">
                        {formatDistanceToNow(new Date(sms.timestamp), {
                          addSuffix: true,
                        })}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {sms.transaction_id}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {sms.sender_number}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {sms.agent_number}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={getOperatorColor(sms.operator)}
                        >
                          {sms.operator}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium text-green-600">
                        ৳{sms.amount.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(sms.status)}>
                          <div className="flex items-center gap-1">
                            {getStatusIcon(sms.status)}
                            <span className="capitalize">{sms.status}</span>
                          </div>
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
